import { useEffect, useRef, useState, type FormEvent } from 'react'
import { ApiClientError, apiClient } from '../api'
import { UiIcon } from './UiIcon'

type PhoneLoginDialogProps = {
  open: boolean
  busy: boolean
  onClose: () => void
  onVerified: () => Promise<void>
}

function validPhone(value: string): boolean {
  return /^\+?[0-9][0-9\s()\-.]{5,23}$/.test(value)
}

// Keep the input forgiving while the user types, but send only a normalized
// value to the API. The API performs the authoritative validation again.
function normalizePhone(value: string): string {
  const compact = value.trim().replace(/[\s()\-.]/g, '')
  return compact.startsWith('00') ? `+${compact.slice(2)}` : compact
}

export function PhoneLoginDialog({ open, busy: authBusy, onClose, onVerified }: PhoneLoginDialogProps) {
  const dialog = useRef<HTMLDialogElement>(null)
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [sent, setSent] = useState(false)
  const [retryAt, setRetryAt] = useState(0)
  const [clock, setClock] = useState(() => Date.now())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  useEffect(() => {
    const element = dialog.current
    if (!element) return
    if (open) {
      if (!element.open) element.showModal()
    } else if (element.open) {
      element.close()
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    setPhone('')
    setCode('')
    setSent(false)
    setRetryAt(0)
    setError('')
    setNotice('')
  }, [open])

  useEffect(() => {
    if (!retryAt) return
    const timer = window.setInterval(() => setClock(Date.now()), 250)
    return () => window.clearInterval(timer)
  }, [retryAt])

  const retrySeconds = Math.max(0, Math.ceil((retryAt - clock) / 1000))
  const normalizedPhone = normalizePhone(phone)
  const phoneReady = validPhone(phone) && normalizedPhone.length <= 20

  const sendCode = async () => {
    if (!phoneReady || busy || authBusy || retrySeconds > 0) return
    setBusy(true)
    setError('')
    setNotice('')
    try {
      const result = await apiClient.sendPhoneCode(normalizedPhone, 'CN')
      // Casdoor invalidates the previous OTP whenever a new one is issued.
      // Never leave an older code in the field after a resend, otherwise it
      // looks valid to the user but can only be rejected by Casdoor.
      setCode('')
      setSent(true)
      setRetryAt(Date.now() + result.retryAfter * 1000)
      setClock(Date.now())
      setNotice(sent
        ? '新验证码已发送，旧验证码已失效，请输入最后一条短信中的验证码'
        : (result.message || '验证码已发送，请查收短信'))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '验证码发送失败，请稍后重试')
    } finally {
      setBusy(false)
    }
  }

  const verifyCode = async (event: FormEvent) => {
    event.preventDefault()
    if (!sent || !/^\d{4,8}$/.test(code.trim()) || busy || authBusy) return
    setBusy(true)
    setError('')
    try {
      await apiClient.verifyPhoneCode(normalizedPhone, code.trim(), 'CN')
      await onVerified()
      onClose()
    } catch (reason) {
      if (reason instanceof ApiClientError) setError(reason.message)
      else setError(reason instanceof Error ? reason.message : '登录失败，请稍后重试')
    } finally {
      setBusy(false)
    }
  }

  return <dialog ref={dialog} className="phone-login-dialog" aria-labelledby="phone-login-title" onCancel={event => { event.preventDefault(); onClose() }}>
    <div className="phone-login-glow" aria-hidden="true" />
    <div className="phone-login-heading">
      <div className="phone-login-icon"><UiIcon name="phone" /></div>
      <div>
        <p className="phone-login-kicker">玩家身份验证</p>
        <h2 id="phone-login-title">手机号登录</h2>
      </div>
      <button className="close-button" type="button" onClick={onClose} aria-label="关闭"><UiIcon name="close" /></button>
    </div>
    <form className="phone-login-body" onSubmit={verifyCode}>
      <p className="phone-login-intro">输入手机号获取验证码。首次验证成功会自动创建账号并登录，不需要单独注册。</p>
      <label htmlFor="phone-login-number">手机号</label>
      <div className="phone-input-row">
        <span className="country-prefix">+86</span>
        <input id="phone-login-number" value={phone} onChange={event => setPhone(event.target.value)} inputMode="tel" autoComplete="tel" placeholder="请输入手机号" maxLength={24} disabled={busy || authBusy} />
      </div>
      <div className="phone-code-row">
        <label className="sr-only" htmlFor="phone-login-code">短信验证码</label>
        <input id="phone-login-code" value={code} onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 8))} inputMode="numeric" autoComplete="one-time-code" placeholder="短信验证码" maxLength={8} disabled={!sent || busy || authBusy} />
        <button className="secondary-button" type="button" onClick={sendCode} disabled={!phoneReady || busy || authBusy || retrySeconds > 0}>{retrySeconds > 0 ? `${retrySeconds}s 后重发` : sent ? '重新获取' : '获取验证码'}</button>
      </div>
      {notice && <p className="phone-login-notice" role="status"><UiIcon name="shield" />{notice}</p>}
      {error && <p className="inline-error" role="alert">{error}</p>}
      <button className="primary-button full phone-login-submit" type="submit" disabled={!sent || !/^\d{4,8}$/.test(code.trim()) || busy || authBusy}>{busy || authBusy ? '正在验证…' : '验证并进入人生'}</button>
      <p className="phone-login-footnote"><UiIcon name="shield" />验证码只用于本次登录，不会保存到浏览器。</p>
    </form>
  </dialog>
}
